import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";

import {
  FaBook,
  FaCloudUploadAlt,
  FaCrop,
  FaFileAlt,
  FaImage,
  FaInfoCircle,
  FaListOl,
  FaSave,
  FaTimes,
  FaTrashAlt,
  FaUpload,
} from "react-icons/fa";

import {
  cropDisciplinePhoto,
  toAbsolutePhotoUrl,
} from "../../../../../../../services/disciplinePhotoServices";

import CropPhotoModal, {
  toMasterUrl,
} from "../../../../../../../components/CropPhotoModal/CropPhotoModal";

import {
  canCropDisciplineImage,
  DISCIPLINE_FALLBACK_IMAGE,
  getDefaultDisciplineForm,
  mapDisciplineToForm,
} from "../utils/disciplinesSettingsUtils";

const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

function DrawerField({ label, icon, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <div className="relative">
        {icon ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            {icon}
          </span>
        ) : null}

        {children}
      </div>

      {hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export default function DisciplineFormDrawer({
  isOpen,
  mode,
  discipline,
  nextOrder,
  submitting,
  uploadProgress,
  onClose,
  onSubmit,
}) {
  const [formData, setFormData] = useState(getDefaultDisciplineForm(nextOrder));
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [outputFormat, setOutputFormat] = useState("webp");

  const [isCropOpen, setIsCropOpen] = useState(false);
  const [imgVersion, setImgVersion] = useState(0);
  const [imgOk, setImgOk] = useState(true);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    setSelectedFile(null);
    setFilePreview("");
    setOutputFormat("webp");
    setIsCropOpen(false);
    setImgOk(true);
    setImgVersion((value) => value + 1);

    if (mode === "edit") {
      setFormData(mapDisciplineToForm(discipline, nextOrder));
    } else {
      setFormData(getDefaultDisciplineForm(nextOrder));
    }
  }, [isOpen, mode, discipline, nextOrder]);

  useEffect(() => {
    if (!selectedFile) {
      setFilePreview("");
      return undefined;
    }

    const url = URL.createObjectURL(selectedFile);
    setFilePreview(url);

    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const title =
    mode === "edit" ? "Modifier la discipline" : "Ajouter une discipline";

  const submitLabel =
    mode === "edit" ? "Enregistrer les modifications" : "Ajouter la discipline";

  const absolutePhoto = useMemo(() => {
    return toAbsolutePhotoUrl(formData.image_discipline);
  }, [formData.image_discipline]);

  const previewUrl = useMemo(() => {
    if (filePreview) return filePreview;
    return absolutePhoto || DISCIPLINE_FALLBACK_IMAGE;
  }, [absolutePhoto, filePreview]);

  const versionedPreviewUrl =
    previewUrl &&
    !previewUrl.startsWith("blob:") &&
    !previewUrl.startsWith("data:")
      ? `${previewUrl}${previewUrl.includes("?") ? "&" : "?"}v=${imgVersion}`
      : previewUrl;

  const canSubmit =
    String(formData.nom_discipline || "").trim() &&
    String(formData.description_discipline || "").trim() &&
    String(formData.ordre_discipline || "").trim() &&
    !submitting;

  const canCrop =
    mode === "edit" && canCropDisciplineImage(formData.image_discipline);

  const cropImageUrl = canCrop
    ? `${toAbsolutePhotoUrl(
        toMasterUrl(formData.image_discipline),
      )}?v=${imgVersion}`
    : "";

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "image_discipline") {
      setSelectedFile(null);
      setImgOk(true);
      setImgVersion((value) => value + 1);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    setImgOk(true);
  };

  const clearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    onSubmit?.({
      formData,
      file: selectedFile,
      outputFormat,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-5xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Discipline
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Renseignez le nom, la description, l’image, le contenu détaillé et
              l’ordre d’affichage public.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="grid gap-5 xl:grid-cols-[280px,1fr]">
              <div className="space-y-4">
                <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                  <div className="mx-auto h-44 w-full max-w-[220px] overflow-hidden rounded-[28px] bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
                    <img
                      src={versionedPreviewUrl}
                      alt={formData.nom_discipline || "Discipline"}
                      className="h-full w-full object-cover"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                      onError={(event) => {
                        event.currentTarget.src = DISCIPLINE_FALLBACK_IMAGE;
                        setImgOk(false);
                      }}
                    />
                  </div>

                  <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
                    Aperçu du visuel de la discipline.
                  </p>

                  {!imgOk ? (
                    <p className="mt-2 rounded-2xl bg-rose-50 px-3 py-2 text-center text-xs font-bold text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
                      Impossible de charger l’image.
                    </p>
                  ) : null}

                  {canCrop ? (
                    <button
                      type="button"
                      onClick={() => setIsCropOpen(true)}
                      disabled={submitting}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      <FaCrop />
                      Recadrer / Zoom
                    </button>
                  ) : mode === "edit" && formData.image_discipline ? (
                    <p className="mt-3 rounded-2xl bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                      Le recadrage est disponible pour les images hébergées sur
                      le serveur, avec un chemin commençant par{" "}
                      <code>/uploads/</code>.
                    </p>
                  ) : null}
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/70">
                  <label className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                    <FaUpload />
                    Téléverser une image
                  </label>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    onChange={handleFileChange}
                    disabled={submitting}
                    className="block w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition file:mr-3 file:rounded-xl file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-bold file:text-indigo-700 hover:file:bg-indigo-100 focus:ring-2 focus:ring-indigo-400 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:file:bg-indigo-500/10 dark:file:text-indigo-300"
                  />

                  <div className="mt-3 flex items-center gap-2">
                    <FaCloudUploadAlt className="text-slate-400" />

                    <select
                      value={outputFormat}
                      onChange={(event) => setOutputFormat(event.target.value)}
                      disabled={submitting}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    >
                      <option value="webp">WEBP recommandé</option>
                      <option value="jpg">JPG</option>
                      <option value="png">PNG</option>
                    </select>
                  </div>

                  {selectedFile ? (
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white px-3 py-2 text-xs text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
                      <span className="min-w-0 truncate">
                        {selectedFile.name} —{" "}
                        {Math.round(selectedFile.size / 1024)} Ko
                      </span>

                      <button
                        type="button"
                        onClick={clearFile}
                        disabled={submitting}
                        className="inline-flex items-center gap-1 rounded-xl bg-rose-50 px-2 py-1 font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:bg-rose-500/10 dark:text-rose-300"
                      >
                        <FaTrashAlt />
                        Retirer
                      </button>
                    </div>
                  ) : null}

                  <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Formats acceptés : PNG, JPG, WEBP. Taille maximale : 10 Mo.
                    Si une URL et un fichier sont fournis, le fichier uploadé
                    sera prioritaire.
                  </p>

                  {submitting && selectedFile ? (
                    <div className="mt-4">
                      <div className="mb-1 flex items-center justify-between text-xs font-bold text-slate-600 dark:text-slate-300">
                        <span>Transfert en cours</span>
                        <span>{uploadProgress}%</span>
                      </div>

                      <div className="h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-[#013E8E] to-[#4CA82F] transition-all"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="md:col-span-2">
                    <DrawerField
                      label="Nom de la discipline *"
                      icon={<FaBook />}
                    >
                      <input
                        type="text"
                        name="nom_discipline"
                        value={formData.nom_discipline}
                        onChange={handleChange}
                        required
                        placeholder="Ex : Judo, Ju-jitsu, Taïso..."
                        className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>
                  </div>

                  <div className="md:col-span-2">
                    <DrawerField
                      label="Description courte *"
                      icon={<FaInfoCircle />}
                    >
                      <input
                        type="text"
                        name="description_discipline"
                        value={formData.description_discipline}
                        onChange={handleChange}
                        required
                        placeholder="Courte description affichée sur le site..."
                        className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                      />
                    </DrawerField>
                  </div>

                  <DrawerField
                    label="Ordre d’affichage *"
                    icon={<FaListOl />}
                    hint="Plus le nombre est petit, plus la discipline apparaît haut dans l’affichage public."
                  >
                    <input
                      type="number"
                      name="ordre_discipline"
                      value={formData.ordre_discipline}
                      onChange={handleChange}
                      required
                      min="0"
                      step="1"
                      className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    />
                  </DrawerField>

                  <DrawerField
                    label="URL de l’image"
                    icon={<FaImage />}
                    hint="Vous pouvez coller une URL externe, un chemin /uploads/... ou laisser vide si vous téléversez une image."
                  >
                    <input
                      type="text"
                      name="image_discipline"
                      value={formData.image_discipline}
                      onChange={handleChange}
                      placeholder="https://... ou /uploads/..."
                      className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    />
                  </DrawerField>
                </div>

                <div>
                  <label className="mb-1 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
                    <FaFileAlt />
                    Contenu détaillé
                  </label>

                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 quill-wrapper">
                    <ReactQuill
                      value={formData.contenu_discipline}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          contenu_discipline: value,
                        }))
                      }
                      theme="snow"
                      placeholder="Décrivez la discipline, ses objectifs, ses bienfaits, les cours proposés..."
                      modules={modules}
                      formats={formats}
                      className="min-h-[240px]"
                    />
                  </div>

                  <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Vous pouvez insérer du texte enrichi, des listes, des liens
                    et des images.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : submitLabel}
              </button>
            </div>
          </div>
        </form>
      </aside>

      {mode === "edit" ? (
        <CropPhotoModal
          isOpen={isCropOpen}
          onClose={() => setIsCropOpen(false)}
          entityId={discipline?.id}
          imageUrl={cropImageUrl}
          onCropped={(response) => {
            const nextUrl =
              typeof response === "string"
                ? response
                : response?.image_discipline ||
                  response?.photo_url ||
                  response?.imageUrl ||
                  response?.url ||
                  response?.path ||
                  response?.data?.image_discipline ||
                  response?.data?.photo_url ||
                  "";

            if (!nextUrl) {
              console.warn("[DisciplineFormDrawer] crop sans URL:", response);
              return;
            }

            setFormData((prev) => ({
              ...prev,
              image_discipline: nextUrl,
            }));

            setSelectedFile(null);
            setImgOk(true);
            setImgVersion((value) => value + 1);
          }}
          format="webp"
          cropAction={cropDisciplinePhoto}
        />
      ) : null}

      <style>{`
        input::placeholder,
        textarea::placeholder {
          color: #94a3b8;
        }

        .dark input::placeholder,
        .dark textarea::placeholder {
          color: #94a3b8;
          opacity: .7;
        }

        .quill-wrapper .ql-toolbar.ql-snow {
          border: 0;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff;
        }

        .quill-wrapper .ql-container.ql-snow {
          border: 0;
          min-height: 240px;
        }

        .quill-wrapper .ql-editor {
          min-height: 240px;
          font-size: 14px;
          line-height: 1.7;
        }

        .dark .quill-wrapper .ql-toolbar.ql-snow {
          background: rgba(15,23,42,.98);
          border-color: #334155;
        }

        .dark .quill-wrapper .ql-container.ql-snow {
          background: rgba(2,6,23,.7);
        }

        .dark .quill-wrapper .ql-editor {
          color: #e2e8f0;
        }

        .dark .ql-snow .ql-stroke {
          stroke: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-fill {
          fill: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-picker,
        .dark .ql-snow .ql-picker-label,
        .dark .ql-snow .ql-picker-options,
        .dark .ql-snow .ql-toolbar button {
          color: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-picker-options {
          background: #0b1220;
          border-color: #334155;
        }

        .dark .ql-snow .ql-toolbar button:hover .ql-stroke {
          stroke: #fff !important;
        }

        .dark .ql-snow .ql-toolbar button:hover .ql-fill {
          fill: #fff !important;
        }

        .quill-wrapper .ql-editor.ql-blank::before {
          color: #64748b;
          opacity: .8;
        }

        .dark .quill-wrapper .ql-editor.ql-blank::before {
          color: #94a3b8;
          opacity: .7;
        }
      `}</style>
    </div>
  );
}
